import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTeams } from '../hooks/useTeams';
import { api } from '../hooks/useApi';
import { Card, Button, Input, Textarea, Select, PageHeader } from '../components/ui';
import { CUSTOMER_TYPES, PRODUCT_GROUPS, DEFAULTS } from '@ns-b2b/shared';

export function VisitForm() {
    const { user } = useAuth();
    const { teams } = useTeams();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [gpsStatus, setGpsStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const placeInputRef = useRef<HTMLInputElement>(null);
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        team: user?.team || 'A',
        rep: user?.name || '',
        customer_name: '',
        phone: '',
        jobsite: '',
        customer_type: '',
        product_groups: [] as string[],
        estimate: '',
        shop: '',
        notes: '',
        latlng: '',
        subdistrict: '',
        district: '',
        province: '',
        map_link: '',
        distance_km: 0,
        gps_accuracy_m: 0,
        photos: [] as string[], // Base64 strings
    });

    // Update team when user is loaded
    useEffect(() => {
        if (user?.team) {
            setFormData(prev => ({ ...prev, team: user.team }));
        }
    }, [user]);

    // Haversine distance calculation
    const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return Math.round(R * c * 10) / 10;
    };

    // Extract address components from Google Place result
    const extractAddress = (components: google.maps.GeocoderAddressComponent[] | undefined) => {
        let subdistrict = '', district = '', province = '';
        components?.forEach((component) => {
            const types = component.types;
            if (types.includes('sublocality_level_2') || types.includes('sublocality')) {
                subdistrict = component.long_name;
            }
            if (types.includes('sublocality_level_1') || types.includes('locality') || types.includes('administrative_area_level_2')) {
                district = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
                province = component.long_name;
            }
        });
        return { subdistrict, district, province };
    };

    // Initialize Google Places Autocomplete
    useEffect(() => {
        const initAutocomplete = () => {
            if (!placeInputRef.current || !window.google?.maps?.places) return;

            const autocomplete = new window.google.maps.places.Autocomplete(placeInputRef.current, {
                componentRestrictions: { country: 'th' },
                fields: ['address_components', 'geometry', 'name', 'formatted_address'],
            });
            autocompleteRef.current = autocomplete;

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (!place?.geometry?.location) return;

                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                const latlng = `${lat},${lng}`;
                const mapLink = `https://www.google.com/maps?q=${lat},${lng}`;

                const { subdistrict, district, province } = extractAddress(place.address_components);

                const originLatlng = '15.7053493,100.053933';
                const [originLat, originLng] = originLatlng.split(',').map(Number);
                const distanceKm = haversineDistance(originLat, originLng, lat, lng);

                setFormData(prev => ({
                    ...prev,
                    jobsite: place.name || place.formatted_address || prev.jobsite,
                    latlng,
                    subdistrict: subdistrict || prev.subdistrict,
                    district: district || prev.district,
                    province: province || prev.province,
                    map_link: mapLink,
                    distance_km: distanceKm,
                }));
                setGpsStatus('success');
            });
        };

        if (window.google?.maps?.places) {
            initAutocomplete();
        } else {
            const checkInterval = setInterval(() => {
                if (window.google?.maps?.places) {
                    initAutocomplete();
                    clearInterval(checkInterval);
                }
            }, 500);
            return () => clearInterval(checkInterval);
        }
    }, []);

    const updateField = (field: string, value: string | string[] | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const toggleProductGroup = (group: string) => {
        setFormData(prev => ({
            ...prev,
            product_groups: prev.product_groups.includes(group)
                ? prev.product_groups.filter(g => g !== group)
                : [...prev.product_groups, group],
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        const files = Array.from(e.target.files);
        const remainingSlots = DEFAULTS.maxPhotosPerVisit - formData.photos.length;

        if (files.length > remainingSlots) {
            alert(`คุณสามารถเพิ่มรูปได้อีก ${remainingSlots} รูปเท่านั้น`);
            return;
        }

        const validFiles: File[] = [];
        for (const file of files) {
            if (!file.type.startsWith('image/')) {
                alert(`ไฟล์ ${file.name} ไม่ใช่รูปภาพ`);
                continue;
            }
            if (file.size > DEFAULTS.maxPhotoSizeMB * 1024 * 1024) {
                alert(`รูป ${file.name} มีขนาดเกิน ${DEFAULTS.maxPhotoSizeMB}MB`);
                continue;
            }
            validFiles.push(file);
        }

        // Convert to Base64
        Promise.all(validFiles.map(file => {
            return new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        })).then(base64Images => {
            setFormData(prev => ({
                ...prev,
                photos: [...prev.photos, ...base64Images],
            }));
            // Reset input
            if (fileInputRef.current) fileInputRef.current.value = '';
        });
    };

    const removePhoto = (index: number) => {
        setFormData(prev => ({
            ...prev,
            photos: prev.photos.filter((_, i) => i !== index),
        }));
    };

    const getGps = () => {
        if (!navigator.geolocation) {
            setError('เบราว์เซอร์ไม่รองรับ GPS');
            return;
        }
        setGpsStatus('loading');
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude, accuracy } = position.coords;
                const latlng = `${latitude},${longitude}`;
                const mapLink = `https://www.google.com/maps?q=${latitude},${longitude}`;

                const originLatlng = '15.7053493,100.053933';
                const [originLat, originLng] = originLatlng.split(',').map(Number);
                const distanceKm = haversineDistance(originLat, originLng, latitude, longitude);

                setFormData(prev => ({
                    ...prev,
                    latlng,
                    gps_accuracy_m: Math.round(accuracy),
                    map_link: mapLink,
                    distance_km: distanceKm,
                }));

                if (window.google?.maps) {
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode(
                        { location: { lat: latitude, lng: longitude } },
                        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
                            if (status === 'OK' && results?.[0]) {
                                const { subdistrict, district, province } = extractAddress(results[0].address_components);
                                setFormData(prev => ({
                                    ...prev,
                                    subdistrict: subdistrict || prev.subdistrict,
                                    district: district || prev.district,
                                    province: province || prev.province,
                                }));
                            }
                        }
                    );
                }
                setGpsStatus('success');
            },
            (err) => {
                setError(`GPS Error: ${err.message}`);
                setGpsStatus('error');
            },
            { enableHighAccuracy: true, timeout: 15000 }
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        if (!formData.customer_name) { setError('กรุณากรอกชื่อลูกค้า'); return; }
        if (!formData.phone) { setError('กรุณากรอกเบอร์โทรศัพท์'); return; }
        if (!formData.latlng) { setError('กรุณากดปุ่ม GPS หรือค้นหาสถานที่'); return; }
        if (formData.notes.length > DEFAULTS.maxNotesLength) {
            setError(`หมายเหตุต้องไม่เกิน ${DEFAULTS.maxNotesLength} ตัวอักษร`);
            return;
        }

        setIsSubmitting(true);
        // Ensure product_groups is sent as comma-separated string if API expects it, 
        // OR as an array if API updated. The previous code joined it.
        // Assuming API expects JSON payload.
        const payload = {
            ...formData,
            product_groups: formData.product_groups.join(','),
            // Photos are already in formData as 'photos' array of base64 strings
        };

        const { error: apiError } = await api('/api/visits', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        setIsSubmitting(false);

        if (apiError) { setError(apiError); return; }

        setSuccess(true);
        setFormData({
            date: new Date().toISOString().split('T')[0],
            team: user?.team || 'A',
            rep: user?.name || '',
            customer_name: '',
            phone: '',
            jobsite: '',
            customer_type: '',
            product_groups: [],
            estimate: '',
            shop: '',
            notes: '',
            latlng: '',
            subdistrict: '',
            district: '',
            province: '',
            map_link: '',
            distance_km: 0,
            gps_accuracy_m: 0,
            photos: [],
        });
        setGpsStatus('idle');
        if (placeInputRef.current) placeInputRef.current.value = '';
    };

    // Check if user has permission to change team (ADMIN or EXECUTIVE)
    const canChangeTeam = ['ADMIN', 'EXECUTIVE'].includes(user?.effectiveRole || '');

    return (
        <div className="max-w-2xl mx-auto">
            <PageHeader
                title="ส่งข้อมูลหน้างาน"
                subtitle="บันทึกข้อมูลการเยี่ยมลูกค้า"
            />

            {success && (
                <div className="bg-success/10 border border-success/20 text-success 
                        px-4 py-3 rounded-xl mb-6 flex items-center gap-3 animate-in">
                    <span className="text-2xl">✓</span>
                    <div>
                        <p className="font-semibold">บันทึกข้อมูลสำเร็จ!</p>
                        <p className="text-sm opacity-80">ข้อมูลถูกบันทึกเรียบร้อยแล้ว</p>
                    </div>
                </div>
            )}

            {error && (
                <div className="bg-error/10 border border-error/20 text-error 
                        px-4 py-3 rounded-xl mb-6 flex items-center gap-3">
                    <span className="text-2xl">!</span>
                    <p className="font-medium">{error}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Section 1: Basic Info */}
                <Card padding="lg">
                    <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm">1</span>
                        ข้อมูลพื้นฐาน
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Input
                            label="วันที่"
                            type="date"
                            value={formData.date}
                            onChange={(e) => updateField('date', e.target.value)}
                            required
                        />
                        <Select
                            label="ทีม"
                            value={formData.team}
                            onChange={(e) => updateField('team', e.target.value)}
                            options={teams.map((t) => ({ value: t.code, label: t.name }))}
                            required
                            disabled={!canChangeTeam}
                        />
                        <Input
                            label="พนักงานขาย"
                            value={formData.rep}
                            onChange={(e) => updateField('rep', e.target.value)}
                            placeholder="ชื่อพนักงาน"
                        />
                    </div>
                </Card>

                {/* Section 2: Location */}
                <Card padding="lg">
                    <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm">2</span>
                        สถานที่
                    </h3>

                    <div className="mb-4">
                        <label className="label">🔍 ค้นหาสถานที่ (Google Places)</label>
                        <input
                            ref={placeInputRef}
                            type="text"
                            placeholder="พิมพ์ชื่อสถานที่, ร้านค้า หรือที่อยู่..."
                            className="input"
                        />
                        <p className="text-xs text-neutral-500 mt-1">
                            เลือกจากผลค้นหาเพื่อเติมข้อมูลอัตโนมัติ
                        </p>
                    </div>

                    <div className="mb-4">
                        <label className="label">📍 หรือใช้ตำแหน่งปัจจุบัน</label>
                        <div className="flex gap-3">
                            <Button
                                type="button"
                                variant={gpsStatus === 'success' ? 'secondary' : 'primary'}
                                onClick={getGps}
                                loading={gpsStatus === 'loading'}
                                className="flex-1"
                            >
                                {gpsStatus === 'loading' ? 'กำลังค้นหาตำแหน่ง...' :
                                    gpsStatus === 'success' ? '✓ ได้ตำแหน่งแล้ว' :
                                        '📍 ปักหมุดตำแหน่งปัจจุบัน'}
                            </Button>
                        </div>
                    </div>

                    {formData.latlng && (
                        <div className="bg-neutral-50 rounded-xl p-4 mb-4 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-neutral-600">พิกัด:</span>
                                <span className="font-mono text-sm">{formData.latlng}</span>
                            </div>
                            {formData.gps_accuracy_m > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-neutral-600">ความแม่นยำ:</span>
                                    <span className="text-sm">{formData.gps_accuracy_m} เมตร</span>
                                </div>
                            )}
                            {formData.distance_km > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-neutral-600">ระยะทาง:</span>
                                    <span className="text-sm font-semibold text-primary">{formData.distance_km} กม.</span>
                                </div>
                            )}
                            {formData.map_link && (
                                <a
                                    href={formData.map_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary hover:underline block mt-2"
                                >
                                    🗺️ เปิดใน Google Maps →
                                </a>
                            )}
                        </div>
                    )}

                    <Input
                        label="ชื่อหน้างาน / สถานที่"
                        value={formData.jobsite}
                        onChange={(e) => updateField('jobsite', e.target.value)}
                        placeholder="เช่น บ้านคุณสมชาย, ร้านค้าเจริญกิจ"
                    />

                    <div className="grid grid-cols-3 gap-4 mt-4">
                        <Input
                            label="ตำบล/แขวง"
                            value={formData.subdistrict}
                            onChange={(e) => updateField('subdistrict', e.target.value)}
                            placeholder="ตำบล"
                        />
                        <Input
                            label="อำเภอ/เขต"
                            value={formData.district}
                            onChange={(e) => updateField('district', e.target.value)}
                            placeholder="อำเภอ"
                        />
                        <Input
                            label="จังหวัด"
                            value={formData.province}
                            onChange={(e) => updateField('province', e.target.value)}
                            placeholder="จังหวัด"
                        />
                    </div>
                </Card>

                {/* Section 3: Customer Info */}
                <Card padding="lg">
                    <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm">3</span>
                        ข้อมูลลูกค้า
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                            label="ชื่อลูกค้า *"
                            value={formData.customer_name}
                            onChange={(e) => updateField('customer_name', e.target.value)}
                            placeholder="ชื่อลูกค้าหรือชื่อร้าน"
                            required
                        />
                        <Input
                            label="เบอร์โทรศัพท์ *"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => updateField('phone', e.target.value)}
                            placeholder="0812345678"
                            required
                        />
                    </div>
                    <div className="mt-4">
                        <Select
                            label="ประเภทลูกค้า"
                            value={formData.customer_type}
                            onChange={(e) => updateField('customer_type', e.target.value)}
                            options={CUSTOMER_TYPES.map((t) => ({ value: t, label: t }))}
                            placeholder="เลือกประเภทลูกค้า"
                        />
                    </div>
                </Card>

                {/* Section 4: Products */}
                <Card padding="lg">
                    <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm">4</span>
                        กลุ่มสินค้าที่สนใจ
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {PRODUCT_GROUPS.map((group) => (
                            <button
                                key={group}
                                type="button"
                                onClick={() => toggleProductGroup(group)}
                                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all
                  ${formData.product_groups.includes(group)
                                        ? 'bg-primary text-white shadow-soft'
                                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                                    }`}
                            >
                                {formData.product_groups.includes(group) && '✓ '}
                                {group}
                            </button>
                        ))}
                    </div>
                </Card>

                {/* Section 5: Additional Info */}
                <Card padding="lg">
                    <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm">5</span>
                        ข้อมูลเพิ่มเติม
                    </h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <Input
                            label="ประมาณการยอด"
                            value={formData.estimate}
                            onChange={(e) => updateField('estimate', e.target.value)}
                            placeholder="เช่น 50,000 บาท"
                        />
                        <Input
                            label="ร้านค้าที่ซื้อ"
                            value={formData.shop}
                            onChange={(e) => updateField('shop', e.target.value)}
                            placeholder="ชื่อร้านค้า"
                        />
                    </div>
                    <Textarea
                        label={`หมายเหตุ (${formData.notes.length}/${DEFAULTS.maxNotesLength})`}
                        value={formData.notes}
                        onChange={(e) => updateField('notes', e.target.value)}
                        placeholder="รายละเอียดเพิ่มเติม..."
                        maxLength={DEFAULTS.maxNotesLength}
                    />
                </Card>

                {/* Section 6: Photos */}
                <Card padding="lg">
                    <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm">6</span>
                        รูปภาพหน้างาน
                    </h3>

                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <label className="label mb-0">อัปโหลดรูปภาพ (สูงสุด {DEFAULTS.maxPhotosPerVisit} รูป)</label>
                            <span className="text-xs text-neutral-500">
                                {formData.photos.length} / {DEFAULTS.maxPhotosPerVisit}
                            </span>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={handleFileChange}
                        />

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {/* Upload Button */}
                            {formData.photos.length < DEFAULTS.maxPhotosPerVisit && (
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="aspect-square rounded-xl border-2 border-dashed border-neutral-300 
                                             flex flex-col items-center justify-center text-neutral-400
                                             hover:border-primary hover:text-primary hover:bg-primary/5 transition-all
                                             focus:outline-none focus:ring-2 focus:ring-primary/20"
                                >
                                    <span className="text-2xl mb-1">📷</span>
                                    <span className="text-xs font-medium">เพิ่มรูปภาพ</span>
                                </button>
                            )}

                            {/* Photo Previews */}
                            {formData.photos.map((photo, index) => (
                                <div key={index} className="aspect-square rounded-xl relative group overflow-hidden bg-neutral-100">
                                    <img
                                        src={photo}
                                        alt={`Preview ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removePhoto(index)}
                                        className="absolute top-1 right-1 w-6 h-6 bg-error text-white rounded-full 
                                                 flex items-center justify-center opacity-0 group-hover:opacity-100
                                                 transition-opacity shadow-sm"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-neutral-500 mt-2">
                            * รองรับไฟล์รูปภาพ jpg, png ขนาดไม่เกิน {DEFAULTS.maxPhotoSizeMB}MB
                        </p>
                    </div>
                </Card>

                {/* Submit Button */}
                <Button
                    type="submit"
                    variant="primary"
                    loading={isSubmitting}
                    className="w-full"
                    size="lg"
                >
                    บันทึกข้อมูลหน้างาน
                </Button>
            </form>
        </div>
    );
}
